<template>
  <div>
    <loading-spinner v-if="loading"> </loading-spinner>
    <template v-else>
      <header-card
        icon="fact_check"
        title="Action Item Management"
        sub-title="Manage action Items in CoreCounts"
      >
        <template v-slot:right>
          <v-row>
            <v-col
              :class="'py-0 my-n2 ' + (i == 0 ? 'pl-0' : '')"
              cols="auto"
              v-for="(item, i) in statuses"
              :key="i"
            >
              <v-card-text class="px-0">
                <action-item-avatar :actionItem="item"></action-item-avatar>
              </v-card-text>
            </v-col>
          </v-row>
        </template>
        <template v-slot:extension>
          <v-divider></v-divider>
          <v-row dense>
            <v-col
              :cols="$vuetify.breakpoint.mdAndUp ? 'auto' : '12'"
              class="my-auto"
            >
              <v-card-text class="py-0">
                <timeframe-select
                  v-model="filter"
                  v-on:change="getActionItems"
                  solo
                  flat
                  :outlined="false"
                  embed
                  hide-custom
                ></timeframe-select>
              </v-card-text>
            </v-col>
            <v-divider vertical v-if="$vuetify.breakpoint.mdAndUp"></v-divider>
            <v-col class="mr-3 pl-2">
              <v-alert
                border="left"
                colored-border
                color="info"
                class="ma-0 subtitle-1 width-fit-content secondarytext--text font-italic"
                dense
                tile
                v-bind:style="{
                  backgroundColor: getColor(
                    $vuetify.theme.themes.light.secondarytext,
                    0.1
                  )
                }"
              >
                The list of action items below is pulled from your meetings. To
                add new, go to your
                <router-link to="/features/meetings" class="info--text"
                  >meeting action items</router-link
                >
              </v-alert>
            </v-col>
          </v-row>
        </template>
      </header-card>
      <v-row>
        <v-col cols="12" v-if="!outstanding_action_items.length">
          <v-card>
            <action-item-line-item
              :user="profile"
              hide-divider
              allow-talking-point
              v-on:talkingpoint="showTalkingPoint(actionItem)"
            ></action-item-line-item>
          </v-card>
        </v-col>
        <template v-else>
          <v-col
            cols="12"
            v-for="actionItem in sorted_action_items(false)"
            :key="actionItem.id"
          >
            <v-card class="overflow-hidden">
              <action-item-line-item
                :actionItem="actionItem"
                allowCompletion
                completeAction="checkin/completeOutstandingActionItem"
                reopenAction="checkin/reopenOutstandingActionItem"
                v-on:edit="editActionItem(actionItem)"
                hide-divider
                v-on:redraw="$emit('redraw')"
                allow-talking-point
                v-on:talkingpoint="showTalkingPoint(actionItem)"
              ></action-item-line-item>
            </v-card>
          </v-col>
          <v-expansion-panels flat>
            <v-expansion-panel class="background">
              <v-expansion-panel-header class="subtitle-1 pa-0 ml-3">
                {{ sorted_action_items(true).length }} Completed Items
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-0">
                <v-col
                  cols="12"
                  v-for="actionItem in sorted_action_items(true)"
                  :key="actionItem.id"
                >
                  <v-card class="overflow-hidden">
                    <action-item-line-item
                      :actionItem="actionItem"
                      allowCompletion
                      completeAction="checkin/completeOutstandingActionItem"
                      reopenAction="checkin/reopenOutstandingActionItem"
                      v-on:edit="editActionItem(actionItem)"
                      hide-divider
                      v-on:redraw="$emit('redraw')"
                      allow-talking-point
                      v-on:talkingpoint="showTalkingPoint(actionItem)"
                    ></action-item-line-item>
                  </v-card>
                </v-col>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
        <edit-action-item-dialog
          v-on:save="getActionItems"
          ref="editActionItem"
        ></edit-action-item-dialog>
        <view-talking-point-dialog
          ref="viewTalkingPoint"
        ></view-talking-point-dialog>
      </v-row>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ActionItemAvatar from "@/components/checkin/ActionItemAvatar";
import ActionItemLineItem from "@/components/checkin/ActionItemLineItem";
import EditActionItemDialog from "@/components/checkin/EditActionItemDialog";
import HeaderCard from "@/components/common/HeaderCard";
import TimeframeSelect from "@/components/reporting/TimeframeSelect";
import ViewTalkingPointDialog from "@/components/checkin/ViewTalkingPointDialog";
import moment from "moment";
export default {
  name: "OutstandingActionItemList",
  components: {
    EditActionItemDialog,
    ActionItemLineItem,
    HeaderCard,
    ActionItemAvatar,
    TimeframeSelect,
    ViewTalkingPointDialog
  },
  data: () => ({
    loading: false,
    filter: null
  }),
  computed: {
    statuses() {
      return [true, null, false].map(x => ({
        completed: x,
        count: this.outstanding_action_items.filter(y => y.completed == x)
          .length
      }));
    },
    sorted_action_items() {
      return complete => {
        return this._.orderBy(
          this.outstanding_action_items.filter(
            x =>
              (!complete && x.completeDate == null) ||
              (complete && x.completeDate != null)
          ),
          ["completeDate", "dueDate"],
          ["desc", "asc"]
        );
      };
    },
    ...mapGetters({
      checkins: "checkin/list",
      profile: "profile/profile",
      outstanding_action_items: "checkin/outstanding_action_items"
    })
  },
  created() {
    this.filter = {
      timeframeId: this.$enums.timeframes.LAST_90_DAYS,
      customStartDate: null,
      customEndDate: null,
      customTimezone: moment.tz.guess(true)
    };
    this.getActionItems();
  },
  methods: {
    getActionItems() {
      this.loading = true;
      this.$store
        .dispatch("checkin/getOutstandingActionItems", this.filter)
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading action items."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    editActionItem(item) {
      this.$store
        .dispatch("checkin/getCheckIn", {
          userId: item.userId
        })
        .then(() => {
          this.$refs.editActionItem.showDialog(item);
        });
    },
    showTalkingPoint(item) {
      this.$refs.viewTalkingPoint.showDialog(item.relatedTalkingPointId);
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .v-expansion-panel-header__icon {
  margin-left: 8px;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}
</style>
